import { useEffect, useRef, useState, type FormEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  CreditCard,
  KeyRound,
  Loader2,
  Plus,
  RotateCcw,
  ShieldCheck,
  Trash2,
  UploadCloud,
} from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { api, jsonBody } from './api'
import { useAuth } from './auth'
import {
  brandContrast,
  DEFAULT_TENANT_BRAND_COLOR,
  isHexColor,
  normalizeHexColor,
} from './branding'
import {
  uploadMediaFile,
  type MediaAsset,
  type UploadStatus,
} from './media-upload'
import { Progress } from './progress'
import type { Page, TenantRole, User } from './types'
import { EmptyState, PageHeader, StatusPill, TableShell } from './ui'

interface Plan {
  id: string
  code: string
  name: string
  deviceLimit: number
  userLimit: number
  storageBytes: number
  active: boolean
}

interface BillingSummary {
  plan: Plan | null
  pendingPlan: Plan | null
  status: string
  trialEndsAt: string | null
  graceEndsAt: string | null
  readOnly: boolean
  deviceLimit: number
  userLimit: number
  storageLimitBytes: number
  usage: { devices: number; users: number; storageBytes: number }
}

interface Member {
  id: string
  tenantId: string
  userId: string
  role: TenantRole
  createdAt: string
  user: User
}

interface Tenant {
  id: string
  name: string
  slug: string
  timezone: string
  logoAssetId: string | null
  logoUrl: string | null
  primaryColor: string | null
  mfaEnforced: boolean
  createdAt: string
}

export function BillingPage() {
  const queryClient = useQueryClient()
  const billing = useQuery({
    queryKey: ['billing-summary'],
    queryFn: () => api<BillingSummary>('/billing/summary'),
  })
  const plans = useQuery({
    queryKey: ['billing-plans'],
    queryFn: () => api<Plan[]>('/billing/plans'),
  })
  const checkout = useMutation({
    mutationFn: (planId: string) =>
      api<{ url: string }>('/billing/checkout', {
        method: 'POST',
        body: jsonBody({ planId }),
      }),
    onSuccess: (result) => window.location.assign(result.url),
    onError: (error) => toast.error(error.message),
  })
  const portal = useMutation({
    mutationFn: () =>
      api<{ url: string }>('/billing/portal', { method: 'POST' }),
    onSuccess: (result) => window.location.assign(result.url),
    onError: (error) => toast.error(error.message),
  })
  const change = useMutation({
    mutationFn: (planId: string) =>
      api('/billing/change-plan', {
        method: 'POST',
        body: jsonBody({ planId }),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['billing-summary'] })
      toast.success('Plan change accepted.')
    },
    onError: (error) => toast.error(error.message),
  })
  const summary = billing.data
  const storagePercent = summary
    ? Math.round((summary.usage.storageBytes / summary.storageLimitBytes) * 100)
    : 0
  return (
    <div className='space-y-7'>
      <PageHeader
        eyebrow='Organization'
        title='Billing & entitlement'
        description='Stripe is the source of truth. Commercial prices come from configured Price IDs; this application never hard-codes money.'
        action={
          <Button
            variant='outline'
            onClick={() => portal.mutate()}
            disabled={portal.isPending}
          >
            <CreditCard className='size-4' />
            Customer portal
          </Button>
        }
      />
      {summary && (
        <div className='grid gap-4 lg:grid-cols-3'>
          <Card className='lg:col-span-2'>
            <CardHeader className='flex-row items-start justify-between'>
              <div>
                <CardTitle>{summary.plan?.name ?? 'Trial'}</CardTitle>
                <p className='mt-2 text-sm text-muted-foreground'>
                  {summary.trialEndsAt
                    ? `Trial ends ${new Date(summary.trialEndsAt).toLocaleDateString()}`
                    : 'Managed by Stripe'}
                </p>
              </div>
              <StatusPill value={summary.status} />
            </CardHeader>
            <CardContent className='grid gap-5 sm:grid-cols-3'>
              {[
                ['Screens', summary.usage.devices, summary.deviceLimit],
                ['Users', summary.usage.users, summary.userLimit],
                [
                  'Storage',
                  Number((summary.usage.storageBytes / 1024 ** 3).toFixed(1)),
                  Number((summary.storageLimitBytes / 1024 ** 3).toFixed(0)),
                ],
              ].map(([label, value, limit]) => (
                <div key={String(label)}>
                  <p className='text-xs font-bold tracking-wider text-muted-foreground uppercase'>
                    {label}
                  </p>
                  <p className='mt-2 text-2xl font-black'>
                    {value}
                    <span className='text-sm font-normal text-muted-foreground'>
                      {' '}
                      / {limit}
                    </span>
                  </p>
                </div>
              ))}
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle className='text-base'>Storage quota</CardTitle>
            </CardHeader>
            <CardContent>
              <p className='text-3xl font-black'>{storagePercent}%</p>
              <Progress value={storagePercent} className='mt-4' />
              <p className='mt-3 text-xs text-muted-foreground'>
                Original objects count toward quota; generated variants do not
                double-charge usage.
              </p>
            </CardContent>
          </Card>
        </div>
      )}
      <div>
        <h2 className='mb-4 text-lg font-bold'>Available plans</h2>
        <div className='grid gap-4 md:grid-cols-3'>
          {plans.data
            ?.filter((plan) => plan.code !== 'trial')
            .map((plan) => (
              <Card
                key={plan.id}
                className={
                  summary?.plan?.id === plan.id ? 'border-primary' : undefined
                }
              >
                <CardHeader>
                  <CardTitle className='text-base'>{plan.name}</CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className='space-y-2 text-sm text-muted-foreground'>
                    <li>{plan.deviceLimit} screens</li>
                    <li>{plan.userLimit} users</li>
                    <li>
                      {(plan.storageBytes / 1024 ** 3).toFixed(0)} GB storage
                    </li>
                  </ul>
                  <Button
                    className='mt-5 w-full'
                    variant={
                      summary?.plan?.id === plan.id ? 'outline' : 'default'
                    }
                    disabled={
                      summary?.plan?.id === plan.id ||
                      checkout.isPending ||
                      change.isPending
                    }
                    onClick={() =>
                      summary?.plan?.code === 'trial'
                        ? checkout.mutate(plan.id)
                        : change.mutate(plan.id)
                    }
                  >
                    {summary?.plan?.id === plan.id
                      ? 'Current plan'
                      : summary?.plan?.code === 'trial'
                        ? 'Subscribe with Stripe'
                        : 'Change plan'}
                  </Button>
                </CardContent>
              </Card>
            ))}
        </div>
      </div>
    </div>
  )
}

export function TeamPage() {
  const queryClient = useQueryClient()
  const [open, setOpen] = useState(false)
  const members = useQuery({
    queryKey: ['members'],
    queryFn: () => api<Page<Member>>('/organization/members?limit=100'),
  })
  const invite = useMutation({
    mutationFn: (input: unknown) =>
      api('/organization/invitations', {
        method: 'POST',
        body: jsonBody(input),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['members'] })
      setOpen(false)
      toast.success('Invitation email queued.')
    },
    onError: (error) => toast.error(error.message),
  })
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const data = new FormData(event.currentTarget)
    invite.mutate({ email: data.get('email'), role: data.get('role') })
  }
  return (
    <div className='space-y-7'>
      <PageHeader
        eyebrow='Organization'
        title='Team & roles'
        description='Invite people into the least-privilege role they need. Ownership and billing remain separate from day-to-day content and device operations.'
        action={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button>
                <Plus className='size-4' />
                Invite person
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Invite a team member</DialogTitle>
                <DialogDescription>
                  The one-time link expires in seven days.
                </DialogDescription>
              </DialogHeader>
              <form className='space-y-4' onSubmit={submit}>
                <div className='space-y-2'>
                  <Label htmlFor='invite-email'>Email</Label>
                  <Input id='invite-email' name='email' type='email' required />
                </div>
                <div className='space-y-2'>
                  <Label htmlFor='invite-role'>Role</Label>
                  <select
                    id='invite-role'
                    name='role'
                    className='h-9 w-full rounded-md border bg-background px-3 text-sm'
                  >
                    <option value='TENANT_ADMIN'>Tenant administrator</option>
                    <option value='CONTENT_MANAGER'>Content manager</option>
                    <option value='DEVICE_MANAGER'>Device manager</option>
                    <option value='VIEWER'>Viewer</option>
                  </select>
                </div>
                <Button className='w-full' disabled={invite.isPending}>
                  {invite.isPending ? 'Inviting…' : 'Send invitation'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        }
      />
      {members.data?.items.length ? (
        <TableShell headers={['Person', 'Role', 'MFA', 'Joined']}>
          {members.data.items.map((member) => (
            <tr key={member.id}>
              <td className='px-4 py-3'>
                <p className='font-semibold'>{member.user.name}</p>
                <p className='mt-1 text-xs text-muted-foreground'>
                  {member.user.email}
                </p>
              </td>
              <td className='px-4 py-3'>
                <StatusPill value={member.role} tone='neutral' />
              </td>
              <td className='px-4 py-3'>
                {member.user.mfaEnabled ? (
                  <span className='inline-flex items-center gap-2 text-emerald-600'>
                    <ShieldCheck className='size-4' />
                    Enabled
                  </span>
                ) : (
                  <span className='text-muted-foreground'>Not enrolled</span>
                )}
              </td>
              <td className='px-4 py-3 text-muted-foreground'>
                {new Date(member.createdAt).toLocaleDateString()}
              </td>
            </tr>
          ))}
        </TableShell>
      ) : (
        <EmptyState
          title='No members'
          description='Invite the first collaborator.'
        />
      )}
    </div>
  )
}

export function BrandingPage() {
  const queryClient = useQueryClient()
  const { session } = useAuth()
  const logoInputRef = useRef<HTMLInputElement>(null)
  const [primaryColor, setPrimaryColor] = useState(DEFAULT_TENANT_BRAND_COLOR)
  const [useCustomColor, setUseCustomColor] = useState(false)
  const [logoAssetId, setLogoAssetId] = useState<string | null>(null)
  const [pendingLogoAssetId, setPendingLogoAssetId] = useState<string>()
  const [logoProgress, setLogoProgress] = useState(0)
  const [logoUploadStatus, setLogoUploadStatus] =
    useState<UploadStatus>('READY')
  const organizationQueryKey = [
    'organization',
    session?.activeTenantId,
  ] as const
  const organization = useQuery({
    queryKey: organizationQueryKey,
    queryFn: () => api<Tenant>('/organization'),
  })
  const media = useQuery({
    queryKey: ['media'],
    queryFn: () => api<Page<MediaAsset>>('/media?limit=100'),
    enabled: Boolean(organization.data),
    refetchInterval: (query) =>
      query.state.data?.items.some((asset) =>
        ['QUEUED', 'PROCESSING'].includes(asset.status)
      )
        ? 5000
        : false,
  })
  const pendingLogo = useQuery({
    queryKey: ['media-asset', pendingLogoAssetId],
    queryFn: () => api<MediaAsset>(`/media/${pendingLogoAssetId}`),
    enabled: Boolean(pendingLogoAssetId),
    refetchInterval: (query) =>
      query.state.data &&
      ['READY', 'FAILED', 'REJECTED'].includes(query.state.data.status)
        ? false
        : 2000,
  })
  const update = useMutation({
    mutationFn: (input: unknown) =>
      api<Tenant>('/organization', { method: 'PATCH', body: jsonBody(input) }),
    onSuccess: (saved) => {
      queryClient.setQueryData(organizationQueryKey, saved)
      toast.success('Organization settings updated.')
    },
    onError: (error) => toast.error(error.message),
  })
  const uploadLogo = useMutation({
    mutationFn: (file: File) =>
      uploadMediaFile(file, (progress, status) => {
        setLogoProgress(progress)
        setLogoUploadStatus(status)
      }),
    onSuccess: async (assetId) => {
      setPendingLogoAssetId(assetId)
      await queryClient.invalidateQueries({ queryKey: ['media'] })
      toast.success('Logo uploaded. Security processing is in progress.')
    },
    onError: (error) => {
      setLogoUploadStatus('ERROR')
      toast.error(error.message)
    },
  })
  const tenant = organization.data
  useEffect(() => {
    if (tenant) {
      setPrimaryColor(tenant.primaryColor ?? DEFAULT_TENANT_BRAND_COLOR)
      setUseCustomColor(Boolean(tenant.primaryColor))
      setLogoAssetId(tenant.logoAssetId)
    }
  }, [tenant])
  useEffect(() => {
    const asset = pendingLogo.data
    if (!asset) return
    if (asset.status === 'READY') {
      setLogoAssetId(asset.id)
      setPendingLogoAssetId(undefined)
      setLogoUploadStatus('DONE')
      void queryClient.invalidateQueries({ queryKey: ['media'] })
      toast.success('Logo is ready and selected. Save settings to apply it.')
    } else if (['FAILED', 'REJECTED'].includes(asset.status)) {
      setPendingLogoAssetId(undefined)
      setLogoUploadStatus('ERROR')
      toast.error('The uploaded logo did not pass media processing.')
    }
  }, [pendingLogo.data, queryClient])

  const normalizedColor = normalizeHexColor(primaryColor)
  const colorValid = isHexColor(normalizedColor)
  const effectiveColor =
    useCustomColor && colorValid ? normalizedColor : DEFAULT_TENANT_BRAND_COLOR
  const contrast = brandContrast(effectiveColor)
  const readyImages =
    media.data?.items.filter(
      (asset) =>
        asset.kind === 'IMAGE' && asset.status === 'READY' && asset.thumbnailUrl
    ) ?? []
  const selectedLogo = readyImages.find((asset) => asset.id === logoAssetId)
  const previewLogoUrl = logoAssetId
    ? (selectedLogo?.thumbnailUrl ??
      (logoAssetId === tenant?.logoAssetId ? tenant.logoUrl : null))
    : null

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (useCustomColor && !colorValid) {
      toast.error('Enter a complete six-digit HEX color, for example #00c278.')
      return
    }
    const data = new FormData(event.currentTarget)
    update.mutate({
      name: data.get('name'),
      timezone: data.get('timezone'),
      logoAssetId,
      primaryColor: useCustomColor ? normalizedColor : null,
      mfaEnforced: data.get('mfaEnforced') === 'on',
    })
  }
  return (
    <div className='space-y-7'>
      <PageHeader
        eyebrow='Organization'
        title='Branding & policy'
        description='Manage the dashboard identity, accessible brand color, logo, timezone, and organization-wide MFA policy.'
      />
      {tenant && (
        <Card className='max-w-5xl'>
          <CardContent className='p-6'>
            <form className='space-y-5' onSubmit={submit}>
              <div className='grid gap-7 lg:grid-cols-[minmax(0,1fr)_320px]'>
                <div className='space-y-6'>
                  <div className='grid gap-4 sm:grid-cols-2'>
                    <div className='space-y-2'>
                      <Label htmlFor='tenant-name'>Organization name</Label>
                      <Input
                        id='tenant-name'
                        name='name'
                        defaultValue={tenant.name}
                        required
                      />
                    </div>
                    <div className='space-y-2'>
                      <Label htmlFor='tenant-timezone'>IANA timezone</Label>
                      <Input
                        id='tenant-timezone'
                        name='timezone'
                        defaultValue={tenant.timezone}
                        required
                      />
                    </div>
                  </div>

                  <section className='space-y-3 rounded-xl border p-4'>
                    <div>
                      <h2 className='font-semibold'>Dashboard logo</h2>
                      <p className='mt-1 text-xs leading-5 text-muted-foreground'>
                        Choose a processed image or upload PNG, JPEG, or WebP.
                        The DOT IT product mark remains unchanged.
                      </p>
                    </div>
                    <Input
                      ref={logoInputRef}
                      type='file'
                      accept='image/jpeg,image/png,image/webp,.jpg,.jpeg,.png,.webp'
                      className='sr-only'
                      aria-label='Upload organization logo'
                      onChange={(event) => {
                        const file = event.target.files?.[0]
                        event.currentTarget.value = ''
                        if (!file) return
                        if (
                          !['image/jpeg', 'image/png', 'image/webp'].includes(
                            file.type
                          ) ||
                          file.size <= 0
                        ) {
                          toast.error(
                            'Choose a non-empty PNG, JPEG, or WebP image.'
                          )
                          return
                        }
                        setLogoProgress(0)
                        setLogoUploadStatus('READY')
                        uploadLogo.mutate(file)
                      }}
                    />
                    <div className='flex flex-col gap-2 sm:flex-row'>
                      <select
                        className='h-9 min-w-0 flex-1 rounded-md border bg-background px-3 text-sm'
                        aria-label='Logo from media library'
                        value={logoAssetId ?? ''}
                        disabled={
                          uploadLogo.isPending || Boolean(pendingLogoAssetId)
                        }
                        onChange={(event) =>
                          setLogoAssetId(event.target.value || null)
                        }
                      >
                        <option value=''>No organization logo</option>
                        {tenant.logoAssetId &&
                          !readyImages.some(
                            (asset) => asset.id === tenant.logoAssetId
                          ) && (
                            <option value={tenant.logoAssetId}>
                              Current organization logo
                            </option>
                          )}
                        {readyImages.map((asset) => (
                          <option key={asset.id} value={asset.id}>
                            {asset.name}
                          </option>
                        ))}
                      </select>
                      <Button
                        type='button'
                        variant='outline'
                        disabled={
                          uploadLogo.isPending || Boolean(pendingLogoAssetId)
                        }
                        onClick={() => logoInputRef.current?.click()}
                      >
                        {uploadLogo.isPending || pendingLogoAssetId ? (
                          <Loader2 className='size-4 animate-spin' />
                        ) : (
                          <UploadCloud className='size-4' />
                        )}
                        {pendingLogoAssetId ? 'Processing…' : 'Upload new logo'}
                      </Button>
                      {logoAssetId && (
                        <Button
                          type='button'
                          size='icon'
                          variant='outline'
                          aria-label='Remove organization logo'
                          onClick={() => setLogoAssetId(null)}
                        >
                          <Trash2 className='size-4' />
                        </Button>
                      )}
                    </div>
                    {(uploadLogo.isPending || pendingLogoAssetId) && (
                      <div>
                        <div className='mb-1 flex justify-between text-xs text-muted-foreground'>
                          <span>
                            {logoUploadStatus === 'HASHING'
                              ? 'Checking file'
                              : logoUploadStatus === 'UPLOADING'
                                ? 'Uploading securely'
                                : 'Validating and creating thumbnail'}
                          </span>
                          <span>{logoProgress}%</span>
                        </div>
                        <Progress value={logoProgress} />
                      </div>
                    )}
                  </section>

                  <section className='space-y-3 rounded-xl border p-4'>
                    <div className='flex flex-wrap items-center justify-between gap-3'>
                      <div>
                        <h2 className='font-semibold'>Dashboard color</h2>
                        <p className='mt-1 text-xs text-muted-foreground'>
                          Used by navigation, primary actions, focus rings, and
                          selected controls.
                        </p>
                      </div>
                      <label className='flex items-center gap-2 text-sm font-medium'>
                        <input
                          type='checkbox'
                          checked={useCustomColor}
                          className='accent-primary'
                          onChange={(event) =>
                            setUseCustomColor(event.target.checked)
                          }
                        />
                        Use custom color
                      </label>
                    </div>
                    <div className='flex flex-col gap-2 sm:flex-row'>
                      <Input
                        id='primary-color'
                        type='color'
                        className='w-full p-1 sm:w-16'
                        aria-label='Primary color'
                        disabled={!useCustomColor}
                        value={colorValid ? normalizedColor : '#000000'}
                        onChange={(event) =>
                          setPrimaryColor(event.target.value)
                        }
                      />
                      <div className='flex-1'>
                        <Label
                          className='sr-only'
                          htmlFor='primary-color-value'
                        >
                          HEX color value
                        </Label>
                        <Input
                          id='primary-color-value'
                          value={primaryColor}
                          disabled={!useCustomColor}
                          aria-label='Current color value'
                          aria-invalid={useCustomColor && !colorValid}
                          placeholder='#00c278'
                          onChange={(event) =>
                            setPrimaryColor(event.target.value)
                          }
                          onBlur={() => {
                            if (colorValid) setPrimaryColor(normalizedColor)
                          }}
                        />
                      </div>
                      <Button
                        type='button'
                        variant='outline'
                        onClick={() => {
                          setUseCustomColor(false)
                          setPrimaryColor(DEFAULT_TENANT_BRAND_COLOR)
                        }}
                      >
                        <RotateCcw className='size-4' />
                        Reset
                      </Button>
                    </div>
                    {useCustomColor && !colorValid ? (
                      <p className='text-xs text-destructive'>
                        Enter six HEX digits, for example #00c278.
                      </p>
                    ) : (
                      <p className='text-xs text-muted-foreground'>
                        Automatic text color: {contrast.foreground} · contrast{' '}
                        {contrast.ratio.toFixed(2)}:1 ·{' '}
                        {contrast.passesAa ? 'WCAG AA pass' : 'WCAG AA warning'}
                      </p>
                    )}
                  </section>

                  <label className='flex items-start gap-3 rounded-lg border p-4'>
                    <input
                      type='checkbox'
                      name='mfaEnforced'
                      defaultChecked={tenant.mfaEnforced}
                      className='mt-1 accent-primary'
                    />
                    <span>
                      <span className='block font-medium'>
                        Require MFA for every member
                      </span>
                      <span className='mt-1 block text-xs text-muted-foreground'>
                        Enroll MFA on your own account first. Existing members
                        must enroll before their next login.
                      </span>
                    </span>
                  </label>
                </div>

                <aside className='self-start rounded-xl border bg-zinc-950 p-4 text-white lg:sticky lg:top-24'>
                  <p className='text-xs font-bold tracking-[0.16em] text-zinc-400 uppercase'>
                    Live dashboard preview
                  </p>
                  <div className='mt-4 flex items-center gap-3'>
                    {previewLogoUrl ? (
                      <img
                        src={previewLogoUrl}
                        alt='Organization logo preview'
                        className='size-12 rounded-lg bg-white object-contain p-1'
                      />
                    ) : (
                      <span
                        className='grid size-12 place-items-center rounded-lg text-lg font-black'
                        style={{
                          backgroundColor: effectiveColor,
                          color: contrast.foreground,
                        }}
                      >
                        {tenant.name.slice(0, 1).toUpperCase()}
                      </span>
                    )}
                    <div className='min-w-0'>
                      <p className='truncate font-semibold'>{tenant.name}</p>
                      <p className='text-xs text-zinc-400'>
                        Dashboard identity
                      </p>
                    </div>
                  </div>
                  <div className='mt-5 space-y-2'>
                    <div
                      className='rounded-lg px-3 py-2 text-sm font-medium'
                      style={{
                        backgroundColor: effectiveColor,
                        color: contrast.foreground,
                      }}
                    >
                      Active navigation
                    </div>
                    <div className='rounded-lg px-3 py-2 text-sm text-zinc-400'>
                      Inactive navigation
                    </div>
                  </div>
                  <button
                    type='button'
                    className='mt-5 h-9 w-full rounded-md text-sm font-semibold'
                    style={{
                      backgroundColor: effectiveColor,
                      color: contrast.foreground,
                    }}
                  >
                    Primary action
                  </button>
                  <p className='mt-4 text-xs leading-5 text-zinc-400'>
                    Preview affects the web dashboard only. Android player and
                    published signage stay unchanged.
                  </p>
                </aside>
              </div>

              <div className='flex flex-wrap items-center gap-3 border-t pt-5'>
                <Button
                  disabled={
                    update.isPending ||
                    uploadLogo.isPending ||
                    Boolean(pendingLogoAssetId) ||
                    (useCustomColor && !colorValid)
                  }
                >
                  {update.isPending ? 'Saving…' : 'Save settings'}
                </Button>
                <span className='text-xs text-muted-foreground'>
                  Save applies branding immediately to every dashboard session.
                </span>
              </div>
            </form>
          </CardContent>
        </Card>
      )}
    </div>
  )
}

export function MfaPage() {
  const { session } = useAuth()
  const [setup, setSetup] = useState<{ secret: string; qrDataUrl: string }>()
  const [codes, setCodes] = useState<string[]>()
  const begin = useMutation({
    mutationFn: () =>
      api<{ secret: string; qrDataUrl: string }>('/auth/mfa/setup', {
        method: 'POST',
      }),
    onSuccess: setSetup,
    onError: (error) => toast.error(error.message),
  })
  const confirm = useMutation({
    mutationFn: (code: string) =>
      api<{ recoveryCodes: string[] }>('/auth/mfa/confirm', {
        method: 'POST',
        body: jsonBody({ code }),
      }),
    onSuccess: (result) => {
      setCodes(result.recoveryCodes)
      toast.success('MFA enabled. Other sessions were revoked.')
    },
    onError: (error) => toast.error(error.message),
  })
  return (
    <div className='space-y-7'>
      <PageHeader
        eyebrow='Security'
        title='Multi-factor authentication'
        description='Platform administrators must use TOTP. Tenant owners can enforce it for their entire organization.'
      />
      <Card className='max-w-xl'>
        <CardContent className='p-6'>
          {codes ? (
            <div>
              <h2 className='font-bold'>Save these recovery codes</h2>
              <p className='mt-2 text-sm text-muted-foreground'>
                Each code works once. They will not be shown again.
              </p>
              <pre className='mt-4 grid grid-cols-2 gap-2 rounded-lg bg-zinc-950 p-4 font-mono text-sm text-white'>
                {codes.join('\n')}
              </pre>
            </div>
          ) : setup ? (
            <form
              className='space-y-4'
              onSubmit={(event) => {
                event.preventDefault()
                confirm.mutate(
                  String(new FormData(event.currentTarget).get('code'))
                )
              }}
            >
              <img
                src={setup.qrDataUrl}
                alt='TOTP enrollment QR code'
                className='mx-auto size-56'
              />
              <p className='rounded-md bg-muted p-3 text-center font-mono text-xs break-all'>
                {setup.secret}
              </p>
              <div className='space-y-2'>
                <Label htmlFor='mfa-code'>Authenticator code</Label>
                <Input
                  id='mfa-code'
                  name='code'
                  inputMode='numeric'
                  pattern='[0-9]{6}'
                  maxLength={6}
                  required
                />
              </div>
              <Button className='w-full'>Confirm MFA</Button>
            </form>
          ) : (
            <div className='text-center'>
              <span className='mx-auto grid size-12 place-items-center rounded-xl bg-red-500/10 text-red-600'>
                <KeyRound className='size-5' />
              </span>
              <h2 className='mt-4 font-bold'>
                {session?.user.mfaEnabled
                  ? 'MFA is enabled'
                  : 'Protect your account'}
              </h2>
              <p className='mt-2 text-sm text-muted-foreground'>
                Use any standards-compatible authenticator app.
              </p>
              <Button
                className='mt-5'
                onClick={() => begin.mutate()}
                disabled={begin.isPending}
              >
                {session?.user.mfaEnabled
                  ? 'Replace authenticator'
                  : 'Begin setup'}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
